import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Tabs,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Alert,
  message,
  theme,
  AutoComplete,
  Spin,
  Table,
  Tag,
  Progress,
  Modal,
  Badge,
  Tooltip,
  Popconfirm,
  Select,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  UserAddOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  MobileOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import debounce from "lodash-es/debounce";
import { STATE_ABBREV_TO_FULL } from "@/libs/useAddressFinder";
import Papa from "papaparse";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import DownstreamSyncTasks from "@components/downstreamSync/DownstreamSyncTasks.react";
import { useStoreCallback } from "@core/hooks/useStore";
import {
  UPLOAD_PARTIAL,
  _UPLOAD_PARTIAL,
  _UPLOAD_PARTIAL_FIN,
} from "@/actions/onboardAction";
import OnboardingStore from "@/stores/OnboardingStore";
import API from "@/utils/APIOnboarding";
import { authenticatedFetch } from "@/libs/api";

const { Title, Text, Paragraph } = Typography;

// Sharp, tech-style input
const inputStyle = { borderRadius: 2 };

// Template download uses API endpoint to work with dev proxy
const TEMPLATE_CSV_URL = "/api/template/onboard";

// State abbreviation mapping
// POW-190: single source of truth, shared with SystemDetail's SiteAddressCard.
// The AddressFinder search/resolve calls below intentionally stay here — this
// page threads selectedAddress through its bulk-upload flow, so extracting them
// would be a restructure of a working page for no functional gain.
const STATE_ABBREV_MAP = STATE_ABBREV_TO_FULL;

// What a successful approval sets off for the customer's Powow APP account (backend downstreamSync)
const appSyncSentence = (mode) => ({
  ON: "Each approved system's customer without a Powow APP account gets one (sent in the background).",
  SHADOW: "APP account sync is in shadow mode: approvals record what would be sent; nothing is sent.",
  PAUSED: "APP account sync is paused: approvals queue accounts, but nothing is sent until it is fixed.",
}[mode] || null);

// One line about the APP account an approval just queued, for the success message
const downstreamNote = (result) => {
  const app = (result?.data?.downstream || []).find((d) => d.target === "APP");
  if (!app) return "";
  return {
    PENDING: " — APP account queued",
    SHADOW: " — APP account recorded (shadow)",
    BLOCKED: " — APP account not queued: data missing (see the Sync tab)",
  }[app.status] || "";
};

export default function Onboard() {
  const { token } = theme.useToken();
  const [form] = Form.useForm();

  // Permission checks
  const roleType = localStorage.getItem("roleType") || "";
  const isSuperadmin = roleType === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  const canApprove = isSuperadmin || userPermissions.includes("system:approve");
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [activeTab, setActiveTab] = useState("single");

  // Address autocomplete state
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Batch upload state
  const [analyzing, setAnalyzing] = useState(false);
  const [csvData, setCsvData] = useState(null); // Analyzed CSV data
  const [csvSummary, setCsvSummary] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Upload progress state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadResults, setUploadResults] = useState(null);
  const [pendingPageSize, setPendingPageSize] = useState(10);
  const [failedPageSize, setFailedPageSize] = useState(10);
  const [succeededPageSize, setSucceededPageSize] = useState(15);

  // Pipeline state
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineData, setPipelineData] = useState({ pending: [], succeeded: [], failed: [] });
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [processingAll, setProcessingAll] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [appActionId, setAppActionId] = useState(null);
  // Mode of the APP-account sync an approval sets off (OFF / SHADOW / ON), for the confirm text
  const [appSyncMode, setAppSyncMode] = useState(null);
  // Sync rows that need an admin (Failed / Missing data / Check manually), for the Sync tab badge
  const [syncProblemCount, setSyncProblemCount] = useState(0);

  const fetchSyncProblemCount = useCallback(() => {
    if (!isSuperadmin) return;
    authenticatedFetch("/api/downstream-sync/tasks?pageSize=1")
      .then((r) => r.json())
      .then((json) => { if (json.success) setSyncProblemCount(json.data.total); })
      .catch(() => {});
  }, [isSuperadmin]);

  useEffect(() => {
    if (!canApprove) return;
    authenticatedFetch("/api/downstream-sync/settings")
      .then((r) => r.json())
      .then((json) => {
        const app = json.success && json.data.targets.find((t) => t.target === "APP");
        if (app) setAppSyncMode(app.paused ? "PAUSED" : app.mode);
      })
      .catch(() => {});
  }, [canApprove]);

  // Controller dropdown state
  const [controllers, setControllers] = useState([]);
  const [controllersLoading, setControllersLoading] = useState(false);

  const inputRef = useRef();

  // Fetch controllers for dropdown
  const fetchControllers = useCallback(async () => {
    setControllersLoading(true);
    try {
      const response = await authenticatedFetch("/api/equipment/controllers");
      const result = await response.json();
      if (result.success) {
        setControllers(result.data);
      }
    } catch (error) {
      console.error("Error fetching controllers:", error);
    } finally {
      setControllersLoading(false);
    }
  }, []);

  // Load controllers on mount
  useEffect(() => {
    fetchControllers();
  }, [fetchControllers]);

  // Fetch pipeline data
  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const response = await authenticatedFetch("/api/onboard/pipeline");
      const result = await response.json();
      if (result.success) {
        setPipelineData(result.data);
        setPipelineSummary(result.summary);
      } else {
        message.error(result.message || "Failed to fetch pipeline");
      }
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      message.error("Failed to fetch pipeline");
    } finally {
      setPipelineLoading(false);
    }
  }, []);

  // Process all pending systems
  const handleProcessAll = async () => {
    setProcessingAll(true);
    try {
      const response = await authenticatedFetch("/api/onboard/process-all", {
        method: "POST",
      });
      const result = await response.json();
      if (result.success) {
        if (result.data?.stopped) message.warning(result.message, 8);
        else message.success(result.message);
        fetchPipeline();
      } else {
        message.error(result.message || "Processing failed");
      }
    } catch (error) {
      message.error("Failed to process systems");
    } finally {
      setProcessingAll(false);
    }
  };

  // Retry a failed system
  // --- APP-submitted systems (onboard_state = PENDING_ADMIN_APPROVAL) ---
  // Approving goes through the app engine first (that is what releases the device data back
  // to the customer's app), then runs the normal onboard. reviewedNmi/reviewedSn echo what
  // this row displayed, so an approval can't land on a submission edited in the meantime.
  const handleApproveAppSubmission = async (row) => {
    setAppActionId(row.id);
    try {
      const response = await authenticatedFetch(`/api/onboard/${row.id}/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewedNmi: row.nmi, reviewedSn: row.sn }),
      });
      const result = await response.json();
      if (result.success) {
        message.success(`Approved ${row.sn} — device data sent to the customer's app`);
      } else {
        message.error(result.message || "Approve failed");
      }
      fetchPipeline();
    } catch (error) {
      message.error("Failed to approve submission");
    } finally {
      setAppActionId(null);
    }
  };

  const handleRejectAppSubmission = (row) => {
    let note = "";
    Modal.confirm({
      title: "Send back for corrections?",
      content: (
        <div>
          <Text type="secondary">
            {row.sn ? `SN ${row.sn}` : ""}{row.nmi ? ` · NMI ${row.nmi}` : ""}
          </Text>
          <Input.TextArea
            rows={3}
            style={{ marginTop: 12 }}
            placeholder="Reason (optional — recorded in the audit trail)"
            maxLength={500}
            onChange={(e) => { note = e.target.value; }}
          />
        </div>
      ),
      okText: "Send back",
      okButtonProps: { danger: true },
      onOk: async () => {
        setAppActionId(`${row.id}:reject`);
        try {
          const response = await authenticatedFetch(`/api/onboard/${row.id}/reject`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ note: note || undefined }),
          });
          const result = await response.json();
          if (result.success) {
            message.success(`${row.sn} sent back to the customer for corrections`);
          } else {
            message.error(result.message || "Reject failed");
          }
          fetchPipeline();
        } catch (error) {
          message.error("Failed to reject submission");
        } finally {
          setAppActionId(null);
        }
      },
    });
  };

  const handleRetry = async (systemId) => {
    setRetryingId(systemId);
    try {
      const response = await authenticatedFetch(`/api/onboard/${systemId}/retry`, {
        method: "POST",
      });
      const result = await response.json();
      if (result.success) {
        message.success(`System onboarded successfully${downstreamNote(result)}`);
        fetchPipeline();
      } else {
        message.error(result.message || "Retry failed");
        fetchPipeline();
      }
    } catch (error) {
      message.error("Failed to retry onboard");
    } finally {
      setRetryingId(null);
    }
  };

  // Fetch pipeline count on mount for badge display
  useEffect(() => {
    fetchPipeline();
  }, []);

  // Refresh pipeline data on tab change
  useEffect(() => {
    if (activeTab === "pipeline" || activeTab === "succeeded") {
      fetchPipeline();
    }
  }, [activeTab, fetchPipeline]);

  // Sync tab badge: on load and whenever the tab changes (approvals may have queued problems)
  useEffect(() => {
    fetchSyncProblemCount();
  }, [activeTab, fetchSyncProblemCount]);

  // AddressFinder API key (frontend widget calls — allowed on Agile plan)
  const AF_KEY = process.env.ADDRESSFINDER_KEY || "";
  const AF_BASE = "https://api.addressfinder.io/api/au/address";

  // Debounced address search (calls AddressFinder directly from browser — widget mode)
  const searchAddresses = useCallback(
    debounce(async (searchText) => {
      if (!searchText || searchText.length < 3) {
        setAddressOptions([]);
        return;
      }

      setAddressSearching(true);
      try {
        const params = new URLSearchParams({
          key: AF_KEY,
          q: searchText,
          format: "json",
          max: "5",
        });
        const response = await fetch(`${AF_BASE}/autocomplete?${params}`);
        const data = await response.json();

        if (data.completions) {
          setAddressOptions(
            data.completions.map((item) => ({
              value: item.full_address,
              label: item.full_address,
              data: { id: item.id, full_address: item.full_address },
            }))
          );
        }
      } catch (error) {
        console.error("Address search failed:", error);
        setAddressOptions([]);
      } finally {
        setAddressSearching(false);
      }
    }, 300),
    []
  );

  // Handle address selection (get full details from AddressFinder)
  const handleAddressSelect = async (value, option) => {
    if (!option?.data?.id) return;

    try {
      const params = new URLSearchParams({
        key: AF_KEY,
        id: option.data.id,
        format: "json",
      });
      const response = await fetch(`${AF_BASE}/info?${params}`);
      const data = await response.json();

      if (data) {
        const addr = {
          full_address: data.full_address,
          address_line_1: data.address_line_1 || data.address_line_combined,
          locality_name: data.locality_name,
          state_territory: data.state_territory,
          postcode: data.postcode,
          latitude: data.latitude || null,
          longitude: data.longitude || null,
        };
        setSelectedAddress(addr);

        // Map state abbreviation to full name
        const stateFull =
          STATE_ABBREV_MAP[addr.state_territory] || addr.state_territory;

        // Update form fields (including the visible address_search field)
        form.setFieldsValue({
          address_search: addr.full_address,
          addressline: addr.full_address,
          street: addr.address_line_1,
          suburb: addr.locality_name,
          state: stateFull,
          postcode: addr.postcode,
        });
      }
    } catch (error) {
      console.error("Failed to get address details:", error);
    }
  };

  useStoreCallback(OnboardingStore, (action) => {
    if (action) {
      if (action.type === _UPLOAD_PARTIAL) {
        // Only for single onboard
      } else if (action.type === _UPLOAD_PARTIAL_FIN) {
        setSubmitting(false);
        if (action.data && activeTab === "single") {
          if (action.data.success || action.data.r === 0) {
            const result = action.data.results?.[0];
            let msg = "System onboarded successfully!";
            if (result) {
              const customerMsg =
                result.customerAction === "created"
                  ? "New customer created"
                  : "Existing customer found";
              const systemMsg =
                result.systemAction === "created"
                  ? "New system created"
                  : "Existing system updated";
              msg = `${customerMsg}. ${systemMsg}.`;
            }
            setSubmitResult({ type: "success", message: msg });
            form.resetFields();
            setSelectedAddress(null);
          } else {
            const errorMsg =
              action.data.errors?.[0]?.error ||
              action.data.message ||
              "Onboard failed";
            setSubmitResult({ type: "error", message: errorMsg });
          }
        }
      }
    }
  });

  // Single onboard form submit
  const handleSingleSubmit = (values) => {
    setSubmitting(true);
    setSubmitResult(null);
    OnboardingStore.dispatch(UPLOAD_PARTIAL({ rows: [values] }));
  };

  // Download template using fetch to avoid routing issues
  const handleDownloadTemplate = async () => {
    try {
      const response = await authenticatedFetch(TEMPLATE_CSV_URL);

      if (!response.ok) {
        message.error("Failed to download template");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "onboard_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download template");
    }
  };

  // Trigger file input
  const handleTriggerUpload = () => {
    if (analyzing || uploading) return;
    inputRef.current.click();
  };

  // Parse and analyze CSV file
  const handleUploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAnalyzing(true);
    setCsvData(null);
    setCsvSummary(null);
    setUploadResults(null);

    try {
      // Parse CSV using PapaParse
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          if (results.errors.length > 0) {
            message.error("CSV parsing error: " + results.errors[0].message);
            setAnalyzing(false);
            return;
          }

          const rows = results.data;
          if (rows.length === 0) {
            message.error("CSV file is empty");
            setAnalyzing(false);
            return;
          }

          // Call analyze API to check existing records
          try {
            const response = await authenticatedFetch("/api/upload/analyze", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ rows }),
            });
            const analyzeResult = await response.json();

            if (analyzeResult.success) {
              setCsvData(analyzeResult.data);
              setCsvSummary(analyzeResult.summary);
              setCurrentPage(1);
            } else {
              message.error(analyzeResult.message || "Analysis failed");
            }
          } catch (err) {
            message.error("Failed to analyze CSV: " + err.message);
          } finally {
            setAnalyzing(false);
          }
        },
        error: (error) => {
          message.error("Failed to parse CSV: " + error.message);
          setAnalyzing(false);
        },
      });
    } catch (error) {
      message.error("Error reading file: " + error.message);
      setAnalyzing(false);
    }

    e.target.value = "";
  };

  // Submit all rows (only non-ignored ones)
  const handleBatchSubmit = async () => {
    if (!csvData || csvData.length === 0) return;

    // Filter out ignored rows
    const uploadableRows = csvData.filter((row) => !row.ignore);
    if (uploadableRows.length === 0) {
      message.error("No uploadable rows (all rows are ignored)");
      return;
    }

    const validRows = uploadableRows;

    setUploading(true);
    setUploadProgress({ current: 0, total: validRows.length });

    const results = {
      success: [],
      failed: [],
    };

    // Upload one by one for progress tracking
    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      setUploadProgress({ current: i + 1, total: validRows.length });

      try {
        // Send analyzed row data with verified address components
        const rowData = {
          ...row.originalRow,
          // Use verified address components from AddressFinder API
          Street: row.street || "",
          Suburb: row.suburb || "",
          State: row.state || "",
          Postcode: row.postcode || "",
          Latitude: row.verifiedAddress?.latitude || null,
          Longitude: row.verifiedAddress?.longitude || null,
        };
        const result = await API.uploadSingleRow(rowData);
        if (result.success || result.r === 0) {
          results.success.push({
            rowIndex: row.rowIndex,
            email: row.email,
            ...result.results?.[0],
          });
        } else {
          results.failed.push({
            rowIndex: row.rowIndex,
            email: row.email,
            error: result.errors?.[0]?.error || result.message || "Unknown error",
          });
        }
      } catch (error) {
        results.failed.push({
          rowIndex: row.rowIndex,
          email: row.email,
          error: error.message,
        });
      }
    }

    setUploading(false);
    setUploadResults(results);

    const customersCreated = results.success.filter((r) => r.customerAction === "created").length;
    const customersFound = results.success.filter((r) => r.customerAction === "found").length;
    const systemsCreated = results.success.filter((r) => r.systemAction === "created").length;
    const systemsFound = results.success.filter((r) => r.systemAction === "found").length;

    // Show summary with styled modal
    Modal.success({
      title: null,
      icon: null,
      width: 480,
      centered: true,
      content: (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          {/* Success Icon */}
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: "0 4px 12px rgba(82, 196, 26, 0.3)",
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 36, color: "#fff" }} />
          </div>

          <Title level={4} style={{ marginBottom: 8 }}>
            Upload Complete
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
            Your batch onboard has been processed successfully
          </Text>

          {/* Stats Grid */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <div
                style={{
                  background: "#f6ffed",
                  borderRadius: 8,
                  padding: "16px 8px",
                  border: "1px solid #b7eb8f",
                }}
              >
                <div style={{ fontSize: 28, fontWeight: 600, color: "#52c41a" }}>
                  {results.success.length}
                </div>
                <div style={{ fontSize: 12, color: "#389e0d" }}>Success</div>
              </div>
            </Col>
            <Col span={8}>
              <div
                style={{
                  background: results.failed.length > 0 ? "#fff2f0" : "#fafafa",
                  borderRadius: 8,
                  padding: "16px 8px",
                  border: `1px solid ${results.failed.length > 0 ? "#ffccc7" : "#d9d9d9"}`,
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 600,
                    color: results.failed.length > 0 ? "#ff4d4f" : "#8c8c8c",
                  }}
                >
                  {results.failed.length}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: results.failed.length > 0 ? "#cf1322" : "#8c8c8c",
                  }}
                >
                  Failed
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div
                style={{
                  background: "#f0f5ff",
                  borderRadius: 8,
                  padding: "16px 8px",
                  border: "1px solid #adc6ff",
                }}
              >
                <div style={{ fontSize: 28, fontWeight: 600, color: "#1890ff" }}>
                  {validRows.length}
                </div>
                <div style={{ fontSize: 12, color: "#096dd9" }}>Total</div>
              </div>
            </Col>
          </Row>

          {/* Details */}
          {results.success.length > 0 && (
            <div
              style={{
                background: "#fafafa",
                borderRadius: 8,
                padding: 16,
                textAlign: "left",
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      CUSTOMERS
                    </Text>
                    <div>
                      <Tag color="green">{customersCreated} created</Tag>
                      <Tag color="blue">{customersFound} found</Tag>
                    </div>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      SYSTEMS
                    </Text>
                    <div>
                      <Tag color="green">{systemsCreated} created</Tag>
                      <Tag color="blue">{systemsFound} updated</Tag>
                    </div>
                  </Space>
                </Col>
              </Row>
            </div>
          )}
        </div>
      ),
      okText: "Done",
      okButtonProps: {
        size: "large",
        style: { borderRadius: 2, minWidth: 120 },
      },
      onOk: () => {
        // Reset state after confirmation
        setCsvData(null);
        setCsvSummary(null);
        setUploadResults(null);
      },
    });
  };

  // Clear CSV data
  const handleClearData = () => {
    setCsvData(null);
    setCsvSummary(null);
    setUploadResults(null);
  };

  // Table columns for batch upload preview
  const columns = [
    {
      title: "#",
      dataIndex: "rowIndex",
      key: "rowIndex",
      width: 50,
    },
    {
      title: "Name",
      key: "name",
      width: 150,
      render: (_, record) =>
        `${record.firstName || ""} ${record.lastName || ""}`.trim() || "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 120,
    },
    {
      title: "NMI",
      dataIndex: "nmi",
      key: "nmi",
      width: 120,
    },
    {
      title: "VPP Controller SN",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 140,
      ellipsis: true,
    },
    {
      title: "Controller",
      dataIndex: "controllerName",
      key: "controllerName",
      width: 120,
      ellipsis: true,
      render: (val) => val || <Text type="secondary">-</Text>,
    },
    {
      title: "Address",
      key: "address",
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        // Show verified address if available, otherwise original
        if (record.verifiedAddress) {
          return record.verifiedAddress.full_address;
        }
        return record.address || record.street || "-";
      },
    },
    {
      title: "Status",
      key: "status",
      width: 180,
      align: "right",
      render: (_, record) => {
        if (record.ignore) {
          return (
            <Tag color="warning" icon={<ExclamationCircleOutlined />}>
              Ignore: {record.ignoreReason}
            </Tag>
          );
        }
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Ready to upload
          </Tag>
        );
      },
    },
  ];

  const tabItems = [
    {
      key: "single",
      label: (
        <span>
          <UserAddOutlined />
          Single Onboard
        </span>
      ),
      children: (
        <Card bordered={false} style={{ borderRadius: 12 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSingleSubmit}
            requiredMark="optional"
          >
            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="first_name"
                  label="First Name"
                  rules={[{ required: true, message: "First Name is required" }]}
                >
                  <Input
                    size="large"
                    placeholder="Enter first name"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[{ required: true, message: "Last Name is required" }]}
                >
                  <Input
                    size="large"
                    placeholder="Enter last name"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Invalid email format" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="email@example.com"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label="Phone"
                  rules={[{ required: true, message: "Phone is required" }]}
                >
                  <Input
                    size="large"
                    placeholder="0412345678"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Address Autocomplete */}
            <Form.Item
              name="address_search"
              label={
                <Space>
                  <EnvironmentOutlined />
                  <span>Address</span>
                </Space>
              }
              rules={[{ required: true, message: "Address is required" }]}
            >
              <AutoComplete
                size="large"
                options={addressOptions}
                onSearch={searchAddresses}
                onSelect={handleAddressSelect}
                placeholder="Start typing to search address..."
                style={{ ...inputStyle, width: "100%" }}
                notFoundContent={addressSearching ? <Spin size="small" /> : null}
              />
            </Form.Item>

            {/* Hidden fields populated from address selection */}
            <Form.Item name="addressline" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="street" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="suburb" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="state" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="postcode" hidden>
              <Input />
            </Form.Item>

            {/* Display selected address details (read-only) */}
            {selectedAddress && (
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  background: token.colorBgTextHover,
                  borderRadius: 2,
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Street
                    </Text>
                    <div>{selectedAddress.address_line_1 || "-"}</div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Suburb
                    </Text>
                    <div>{selectedAddress.locality_name || "-"}</div>
                  </Col>
                  <Col span={12} style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      State
                    </Text>
                    <div>{selectedAddress.state_territory || "-"}</div>
                  </Col>
                  <Col span={12} style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Postcode
                    </Text>
                    <div>{selectedAddress.postcode || "-"}</div>
                  </Col>
                </Row>
              </Card>
            )}

            <Form.Item
              name="nmi"
              label="NMI"
              rules={[
                { required: true, message: "NMI is required" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const compact = value.replace(/\s/g, "").toUpperCase();
                    if (!/^[A-Z0-9]{10,11}$/.test(compact)) {
                      return Promise.reject("NMI must be 10 or 11 letters/digits");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                size="large"
                placeholder="10 or 11 character NMI"
                style={inputStyle}
              />
            </Form.Item>

            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="controller_name"
                  label="VPP Controller"
                  rules={[{ required: true, message: "Controller is required" }]}
                >
                  <Select
                    size="large"
                    placeholder="Select controller type"
                    style={inputStyle}
                    loading={controllersLoading}
                    options={controllers.map((c) => ({
                      value: c.name,
                      label: (
                        <Space>
                          <span>{c.name}</span>
                          <Tag color="default" style={{ fontSize: 10 }}>{c.vendor}</Tag>
                        </Space>
                      ),
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="powow_meter_sn"
                  label="Controller Serial Number"
                  dependencies={["controller_name"]}
                  rules={[
                    {
                      required: true,
                      message: "Serial Number is required",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value) return Promise.resolve();
                        const controllerName = getFieldValue("controller_name");
                        if (
                          controllerName &&
                          controllerName.toLowerCase() === "powow meter" &&
                          !/^TSM\d{10}$/.test(value)
                        ) {
                          return Promise.reject(
                            new Error("Powow meter SN must start with 'TSM' followed by exactly 10 digits")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter serial number"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="controller_description"
              label="Controller Description (optional)"
            >
              <Input.TextArea
                rows={2}
                placeholder="Any additional notes about this controller installation"
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                icon={<UserAddOutlined />}
                style={inputStyle}
              >
                Onboard System
              </Button>
            </Form.Item>
          </Form>

          {submitResult && (
            <Alert
              message={submitResult.message}
              type={submitResult.type}
              showIcon
              style={{ marginTop: 24, borderRadius: 2 }}
            />
          )}
        </Card>
      ),
    },
    {
      key: "batch",
      label: (
        <span>
          <CloudUploadOutlined />
          Batch Onboard
        </span>
      ),
      children: (
        <div>
          {/* Step 1: Download template and upload CSV */}
          {!csvData && (
            <Row gutter={24}>
              {/* Download Template Card */}
              <Col xs={24} md={12}>
                <Card
                  bordered={false}
                  style={{ borderRadius: 12, textAlign: "center", height: "100%" }}
                >
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: `rgba(84, 162, 204, 0.1)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <FileTextOutlined
                      style={{ fontSize: 36, color: token.colorPrimary }}
                    />
                  </div>
                  <Title level={4} style={{ marginBottom: 12 }}>
                    Download Template
                  </Title>
                  <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                    Download the CSV template and fill in your system data. Ensure
                    all required fields are completed.
                  </Paragraph>
                  <Button
                    size="large"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                    style={inputStyle}
                  >
                    Download CSV Template
                  </Button>
                </Card>
              </Col>

              {/* Upload CSV Card */}
              <Col xs={24} md={12}>
                <Card
                  bordered={false}
                  style={{ borderRadius: 12, textAlign: "center", height: "100%" }}
                >
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: `rgba(84, 162, 204, 0.1)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <UploadOutlined
                      style={{ fontSize: 36, color: token.colorPrimary }}
                    />
                  </div>
                  <Title level={4} style={{ marginBottom: 12 }}>
                    Upload CSV
                  </Title>
                  <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                    Upload your completed CSV file. The data will be analyzed and
                    displayed for review before submission.
                  </Paragraph>
                  <Button
                    type="primary"
                    size="large"
                    icon={<UploadOutlined />}
                    onClick={handleTriggerUpload}
                    loading={analyzing}
                    style={inputStyle}
                  >
                    {analyzing ? "Analyzing..." : "Select CSV File"}
                  </Button>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleUploadFile}
                    style={{ display: "none" }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Step 2: Show analyzed data in table */}
          {csvData && (
            <Card bordered={false} style={{ borderRadius: 12 }}>
              {/* Summary */}
              {csvSummary && (
                <Alert
                  type="info"
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  message={
                    <Space size="large">
                      <span>
                        <strong>Total:</strong> {csvSummary.total}
                      </span>
                      <span style={{ color: "#52c41a" }}>
                        <strong>Uploadable:</strong> {csvSummary.uploadable}
                      </span>
                      <span style={{ color: "#faad14" }}>
                        <strong>Ignored:</strong> {csvSummary.ignored}
                      </span>
                      <span>
                        <strong>Existing Customers:</strong>{" "}
                        {csvSummary.existingCustomers}
                      </span>
                      <span>
                        <strong>Existing Systems:</strong>{" "}
                        {csvSummary.existingSystems}
                      </span>
                    </Space>
                  }
                  style={{ marginBottom: 16, borderRadius: 2 }}
                />
              )}

              {/* Data Table */}
              <Table
                columns={columns}
                dataSource={csvData}
                rowKey="rowIndex"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: csvData.length,
                  onChange: (page) => setCurrentPage(page),
                  showSizeChanger: false,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} rows`,
                }}
                scroll={{ x: 1200 }}
                size="small"
                rowClassName={(record) =>
                  record.ignore ? "ant-table-row-ignored" : ""
                }
              />

              {/* Action buttons */}
              <div
                style={{
                  marginTop: 24,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Button onClick={handleClearData} style={inputStyle}>
                  Cancel / Upload Different File
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<CloudUploadOutlined />}
                  onClick={handleBatchSubmit}
                  disabled={!csvSummary || csvSummary.uploadable === 0}
                  style={inputStyle}
                >
                  Submit {csvSummary?.uploadable || 0} Uploadable Records
                </Button>
              </div>
            </Card>
          )}

          {/* Upload Progress Modal */}
          <Modal
            open={uploading}
            title="Uploading Records..."
            footer={null}
            closable={false}
            centered
            maskClosable={false}
          >
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Progress
                type="circle"
                percent={Math.round(
                  (uploadProgress.current / uploadProgress.total) * 100
                )}
                format={() =>
                  `${uploadProgress.current} / ${uploadProgress.total}`
                }
              />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  Processing record {uploadProgress.current} of{" "}
                  {uploadProgress.total}...
                </Text>
              </div>
            </div>
          </Modal>
        </div>
      ),
    },
    {
      key: "pipeline",
      label: (
        <span>
          <ClockCircleOutlined />
          Pipeline
          {pipelineSummary && (pipelineSummary.pendingCount + pipelineSummary.failedCount) > 0 && (
            <Badge
              count={pipelineSummary.pendingCount + pipelineSummary.failedCount}
              style={{ marginLeft: 8, backgroundColor: "#faad14" }}
            />
          )}
        </span>
      ),
      children: (
        <Card bordered={false} style={{ borderRadius: 12 }}>
          {/* Pipeline Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Space>
              <Text strong>Onboard Pipeline</Text>
              <Text type="secondary">Systems awaiting the device data check</Text>
            </Space>
            <Space>
              <Button
                icon={<ReloadOutlined spin={pipelineLoading} />}
                onClick={fetchPipeline}
                disabled={pipelineLoading}
              >
                Refresh
              </Button>
              {canApprove && (
                <Popconfirm
                  title="Process all pending systems?"
                  description={(
                    <div style={{ maxWidth: 320 }}>
                      Approves each pending system whose data reached the platform in the last 3 days. The rest are marked failed with the reason.
                      {appSyncSentence(appSyncMode) && <div style={{ marginTop: 6 }}>{appSyncSentence(appSyncMode)}</div>}
                    </div>
                  )}
                  onConfirm={handleProcessAll}
                  okText="Process All"
                  cancelText="Cancel"
                >
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    loading={processingAll}
                    disabled={!pipelineData.pending?.length}
                  >
                    Process All Pending
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </div>

          {/* Pending Systems */}
          {pipelineData.pending?.length > 0 && (
            <>
              <Alert
                type="info"
                icon={<ClockCircleOutlined />}
                message={`${pipelineData.pending.length} system(s) pending the device data check`}
                style={{ marginBottom: 16, borderRadius: 2 }}
              />
              <Table
                columns={[
                  { title: "SN", dataIndex: "sn", key: "sn", width: 150, render: (sn) => <Text code>{sn}</Text> },
                  { title: "NMI", dataIndex: "nmi", key: "nmi", width: 120 },
                  { title: "Customer", dataIndex: "customer_name", key: "customer_name", width: 150 },
                  { title: "Company", dataIndex: "company_name", key: "company_name", width: 150 },
                  {
                    title: "Address",
                    key: "address",
                    width: 200,
                    render: (_, r) =>
                      r.from_app
                        ? [r.street, r.suburb, r.state, r.postcode].filter(Boolean).join(", ") || "-"
                        : "-",
                  },
                  { title: "Onboard Date", dataIndex: "onboard_date", key: "onboard_date", width: 120,
                    render: (date) => date ? new Date(date).toLocaleDateString() : "-" },
                  { title: "Status", key: "status", width: 120,
                    render: (_, r) => r.from_app
                      ? <Tag icon={<MobileOutlined />} color="gold">From App</Tag>
                      : <Tag icon={<ClockCircleOutlined />} color="processing">Pending</Tag> },
                  {
                    // App submissions need a per-system decision (does this customer really own
                    // this controller SN?), so they are actioned here rather than by the batch
                    // button. Approving also releases the device data back to the customer's app.
                    title: "Action",
                    key: "action",
                    width: 190,
                    render: (_, r) =>
                      r.from_app ? (
                        <Space size={4}>
                          <Button
                            type="primary"
                            size="small"
                            loading={appActionId === r.id}
                            onClick={() => handleApproveAppSubmission(r)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            danger
                            loading={appActionId === `${r.id}:reject`}
                            onClick={() => handleRejectAppSubmission(r)}
                          >
                            Reject
                          </Button>
                        </Space>
                      ) : (
                        <Tooltip title="Use Process All Pending for portal-onboarded systems">
                          <span><Button size="small" disabled>—</Button></span>
                        </Tooltip>
                      ),
                  },
                ]}
                dataSource={pipelineData.pending}
                rowKey="id"
                size="small"
                pagination={makePagination(pendingPageSize, setPendingPageSize)}
                loading={pipelineLoading}
              />
            </>
          )}

          {/* Failed Systems */}
          {pipelineData.failed?.length > 0 && (
            <>
              <Alert
                type="error"
                icon={<WarningOutlined />}
                message={`${pipelineData.failed.length} system(s) failed the device data check`}
                style={{ marginTop: 24, marginBottom: 16, borderRadius: 2 }}
              />
              <Table
                columns={[
                  { title: "SN", dataIndex: "sn", key: "sn", width: 150, render: (sn) => <Text code>{sn}</Text> },
                  { title: "NMI", dataIndex: "nmi", key: "nmi", width: 120 },
                  { title: "Customer", dataIndex: "customer_name", key: "customer_name", width: 150 },
                  { title: "Error", dataIndex: "onboard_api_message", key: "onboard_api_message",
                    render: (msg) => <Text type="danger">{msg || "Unknown error"}</Text> },
                  { title: "Trial Date", dataIndex: "onboard_trial_date", key: "onboard_trial_date", width: 140,
                    render: (date) => date ? new Date(date).toLocaleString() : "-" },
                  ...(canApprove ? [{ title: "Action", key: "action", width: 100,
                    render: (_, record) => (
                      <Button
                        size="small"
                        icon={<SyncOutlined />}
                        loading={retryingId === record.id}
                        onClick={() => handleRetry(record.id)}
                      >
                        Retry
                      </Button>
                    ),
                  }] : []),
                ]}
                dataSource={pipelineData.failed}
                rowKey="id"
                size="small"
                pagination={makePagination(failedPageSize, setFailedPageSize)}
                loading={pipelineLoading}
              />
            </>
          )}

          {/* Empty State */}
          {!pipelineLoading && !pipelineData.pending?.length && !pipelineData.failed?.length && (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <CheckCircleOutlined style={{ fontSize: 48, color: token.colorSuccess, marginBottom: 16 }} />
              <Title level={4}>All Clear!</Title>
              <Text type="secondary">No systems pending verification</Text>
            </div>
          )}
        </Card>
      ),
    },
    {
      key: "succeeded",
      label: (
        <span>
          <CheckCircleOutlined />
          Succeeded
          {pipelineSummary?.succeededCount > 0 && (
            <Badge
              count={pipelineSummary.succeededCount}
              style={{ marginLeft: 8, backgroundColor: token.colorSuccess }}
            />
          )}
        </span>
      ),
      children: (
        <Card bordered={false} style={{ borderRadius: 12 }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Space>
              <Text strong>Successfully Onboarded</Text>
              <Text type="secondary">Systems verified with device configuration</Text>
            </Space>
            <Button
              icon={<ReloadOutlined spin={pipelineLoading} />}
              onClick={fetchPipeline}
              disabled={pipelineLoading}
            >
              Refresh
            </Button>
          </div>

          <Table
            columns={[
              { title: "SN", dataIndex: "sn", key: "sn", width: 150, render: (sn) => <Text code>{sn}</Text> },
              { title: "NMI", dataIndex: "nmi", key: "nmi", width: 120 },
              { title: "Customer", dataIndex: "customer_name", key: "customer_name", width: 150 },
              { title: "Controller", dataIndex: "controller_name", key: "controller_name", width: 120,
                render: (name) => name ? <Tag color="blue">{name}</Tag> : "-" },
              { title: "Inverter kW", dataIndex: "inverter_kw", key: "inverter_kw", width: 100,
                render: (kw) => kw ? `${parseFloat(kw).toFixed(1)} kW` : "-" },
              { title: "Battery kWh", dataIndex: "battery_kwh", key: "battery_kwh", width: 100,
                render: (kwh) => kwh ? `${parseFloat(kwh).toFixed(1)} kWh` : "-" },
              { title: "Success Date", dataIndex: "onboard_success_date", key: "onboard_success_date", width: 140,
                render: (date) => date ? new Date(date).toLocaleString() : "-" },
              { title: "Status", key: "status", width: 100,
                render: () => <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag> },
            ]}
            dataSource={pipelineData.succeeded}
            rowKey="id"
            size="small"
            pagination={makePagination(succeededPageSize, setSucceededPageSize)}
            loading={pipelineLoading}
            locale={{ emptyText: "No successfully onboarded systems yet" }}
          />
        </Card>
      ),
    },
    // What approvals set off downstream (the customer's APP account) — admin only
    ...(isSuperadmin ? [{
      key: "sync",
      label: (
        <span>
          <SyncOutlined />
          Sync
          {syncProblemCount > 0 && (
            <Badge count={syncProblemCount} style={{ marginLeft: 8 }} />
          )}
        </span>
      ),
      children: <DownstreamSyncTasks onChanged={fetchSyncProblemCount} />,
    }] : []),
  ];

  return (
    <PageLayout active="onboard">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Onboard Systems
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Add new systems to the VPP individually or in batch
        </Text>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />

      <style>{`
        .ant-table-row-ignored {
          background-color: #fffbe6 !important;
        }
        .ant-table-row-ignored:hover > td {
          background-color: #fff7cc !important;
        }
        .ant-table-row-ignored td {
          color: #8c8c8c;
        }
      `}</style>
    </PageLayout>
  );
}
